import { getSessionToken, clearSessionToken } from '../components/auth/LoginScreen.js';

const BASE = '/api';

interface FetchOptions {
  method?: 'GET' | 'POST' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
}

class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, options: FetchOptions = {}): Promise<T> {
  const { method = 'GET', body, signal } = options;

  const headers: Record<string, string> = {};
  if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }

  // Attach session token to all API requests
  const token = getSessionToken();
  if (token) {
    headers['x-session-token'] = token;
  }

  const response = await fetch(`${BASE}${path}`, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    signal,
    cache: 'no-store',
  });

  if (!response.ok) {
    // A single 401 on a background poll must NOT nuke the session — it may be a
    // transient blip (backend restart, proxy hiccup). Only log out if a direct
    // session check confirms the token is really invalid.
    if (response.status === 401 && (await sessionReallyInvalid())) {
      clearSessionToken();
      window.location.reload();
    }
    const text = await response.text().catch(() => 'Unknown error');
    throw new ApiError(response.status, text);
  }

  return response.json() as Promise<T>;
}

/** Verify with the server whether the current token is genuinely rejected.
 *  Returns true only on a confirmed 401; network errors keep the session. */
async function sessionReallyInvalid(): Promise<boolean> {
  const token = getSessionToken();
  if (!token) return true;
  try {
    const r = await fetch(`${BASE}/auth/check`, {
      method: 'POST',
      headers: { 'x-session-token': token },
      cache: 'no-store',
    });
    return r.status === 401;
  } catch {
    return false; // network error — don't log the user out
  }
}

export const api = {
  // Farms
  getFarms: (signal?: AbortSignal) =>
    request<FarmResponse[]>('/farms', { signal }),

  getFarm: (id: number, signal?: AbortSignal) =>
    request<FarmResponse>(`/farms/${id}`, { signal }),

  // Pool
  getPool: (signal?: AbortSignal) =>
    request<PoolResponse>('/pool', { signal }),

  // Rates
  getRates: (ids?: string[], signal?: AbortSignal) => {
    const params = ids?.length ? `?ids=${ids.join(',')}` : '';
    return request<CoinMarket[]>(`/rates${params}`, { signal });
  },

  getCoinsList: (signal?: AbortSignal) =>
    request<CoinListItem[]>('/rates/coins', { signal }),

  // Registry
  getRegistry: (signal?: AbortSignal) =>
    request<RegistryItem[]>('/registry', { signal }),

  searchRegistry: (q: string, page: number, limit = 30, signal?: AbortSignal) =>
    request<RegistrySearchResult>(`/registry/search?q=${encodeURIComponent(q)}&page=${page}&limit=${limit}`, { signal }),

  getRegistryStats: (signal?: AbortSignal) =>
    request<{ total: number; visible: number }>('/registry/stats', { signal }),

  toggleCoin: (id: string, visible: boolean) =>
    request<{ ok: boolean }>('/registry/toggle', { method: 'POST', body: { id, visible } }),

  saveRegistry: (items: RegistryItem[]) =>
    request<{ ok: boolean; count: number }>('/registry', { method: 'POST', body: items }),

  // Health
  getHealth: () =>
    request<HealthResponse>('/health'),

  // Alerts
  getAlertSettings: (signal?: AbortSignal) =>
    request<AlertSettingsResponse>('/alerts/settings', { signal }),

  saveAlertSettings: (settings: AlertSettingsSave) =>
    request<{ ok: boolean }>('/alerts/settings', { method: 'POST', body: settings }),

  testAlert: () =>
    request<{ ok: boolean }>('/alerts/test', { method: 'POST' }),

  // Energy History
  getEnergyMonthly: (months = 6, signal?: AbortSignal) =>
    request<EnergyMonth[]>(`/energy/month?months=${months}`, { signal }),

  getEnergyDaily: (signal?: AbortSignal) =>
    request<EnergyDay[]>('/energy', { signal }),

  // User Settings
  getSettings: (signal?: AbortSignal) =>
    request<UserSettings>('/settings', { signal }),

  saveSettings: (settings: UserSettings) =>
    request<UserSettings>('/settings', { method: 'POST', body: settings }),

  // Spot
  getSpot: (signal?: AbortSignal) =>
    request<SpotSnapshot>('/spot', { signal }),

  savePosition: (position: SpotPositionInput) =>
    request<SpotPosition>('/spot/positions', { method: 'POST', body: position }),

  deletePosition: (id: string) =>
    request<{ ok: boolean; id: string }>(`/spot/positions/${encodeURIComponent(id)}`, { method: 'DELETE' }),

  resetPositionTp: (id: string) =>
    request<{ ok: boolean }>(`/spot/positions/${encodeURIComponent(id)}/reset-tp`, { method: 'POST' }),

  recordStage: (id: string, stage: SpotStageInput) =>
    request<SpotPosition>(`/spot/positions/${encodeURIComponent(id)}/stages`, { method: 'POST', body: stage }),

  removeStageEntry: (id: string, entryId: string) =>
    request<SpotPosition>(`/spot/positions/${encodeURIComponent(id)}/stage-entries/${encodeURIComponent(entryId)}`, { method: 'DELETE' }),

  saveSpotConfig: (config: SpotConfigInput) =>
    request<SpotConfig>('/spot/config', { method: 'POST', body: config }),
};

// ── Types ──────────────────────────────────────────────────

export interface FarmResponse {
  id: number;
  name: string;
  hello: HiveHello;
  stats: HiveStats | null;
  statsMtime: string | null;
}

export interface HiveHello {
  params: {
    gpu: GpuInfo[];
    boot_time: number;
    mb: { manufacturer: string; product: string };
    cpu: { model: string; cores: number };
    disk_model: string;
    nvidia_version: string;
    amd_version: string;
  };
}

export interface GpuInfo {
  brand: string;
  name: string;
  busid: string;
  mem: string;
  mem_type: string;
  subvendor: string;
}

export interface HiveMinerStats {
  hs?: number[];
  hs_units?: string;
  temp?: number[];
  fan?: number[];
  bus_numbers?: number[];
  ar?: number[];
  algo?: string;
  ver?: string;
  uptime?: number;
  total_khs?: number;
}

export interface HiveStats {
  params: {
    // A rig can run two miners at once (e.g. AMD + Nvidia) — the second set is
    // reported under the `*2` keys. total_khs/total_khs2 are per-miner kH/s totals.
    miner?: string;
    miner2?: string;
    miner_stats?: HiveMinerStats;
    miner_stats2?: HiveMinerStats;
    total_khs?: number;
    total_khs2?: number;
    power?: number[];
    temp?: number[];
    mtemp?: number[];
    fan?: number[];
  };
}

export interface PoolResponse {
  pool: {
    coinSymbol: string;
    coinPriceUsd: string;
  };
  miner: {
    totalBlocksFound: number;
    curHashrate: number;
    curHashrateStr: string;
    payments: Payment[];
    sumrewards: SumReward[];
    blocksSumRewards: BlocksSumReward[];
    workersOnline: number;
    workersOffline: number;
  };
  _lastModified: string;
}

export interface Payment {
  timestamp: number;
  amount: number;
  tx: string;
}

export interface SumReward {
  reward: number;
}

export interface BlocksSumReward {
  reward: number;
  variance: number;
}

export interface CoinMarket {
  id: string;
  symbol: string;
  name: string;
  image: string;
  current_price: number;
  price_change_percentage_24h: number | null;
  market_cap: number | null;
  /** Set when Bybit and CoinGecko prices for this coin diverge beyond the threshold. */
  priceDivergencePct?: number;
}

export interface CoinListItem {
  id: string;
  symbol: string;
  name: string;
}

export interface RegistrySearchResult {
  items: RegistryItem[];
  total: number;
  page: number;
  pages: number;
}

export interface RegistryItem {
  id: string;
  symbol: string;
  name: string;
  visible: boolean;
}

export interface HealthResponse {
  status: string;
  uptime: number;
  timestamp: string;
  data: Record<string, unknown>;
}

export interface EnergyMonth {
  month: string;
  days: number;
  totalKwh: number;
  totalCostRub: number;
  totalCostUsd: number;
  totalRevenueUsd: number;
  totalProfitUsd: number;
  avgPowerW: number;
  uptimeHours: number;
  downtimeHours: number;
}

export interface EnergyDay {
  date: string;
  avgPowerW: number;
  kwh: number;
  costRub: number;
  costUsd: number;
  revenueUsd: number;
  profitUsd: number;
  electricityRate: number;
  onlineRigs: number;
  offlineRigs: number;
  uptimeHours: number;
  downtimeHours: number;
  blocksToday?: number;
}

export interface UserSettings {
  theme: string;
  offlineTimeout: number;
  coreTempThreshold: number;
  memoryTempThreshold: number;
  electricityRate: number;
}

export interface AlertSettingsResponse {
  telegramBotToken: string;
  telegramChatId: string;
  enableOfflineAlert: boolean;
  enableTempAlert: boolean;
  enableHashrateDropAlert: boolean;
  hashrateDropThreshold: number;
}

export interface AlertSettingsSave {
  telegramBotToken: string;
  telegramChatId: string;
  enableOfflineAlert: boolean;
  enableTempAlert: boolean;
  enableHashrateDropAlert: boolean;
  hashrateDropThreshold: number;
}

// ── Spot ────────────────────────────────────────────────────

export interface SpotTpStatus {
  price: number | null;
  reached: boolean;
  progressPct: number;
  distancePct: number | null;
  profitAtTpUsd: number | null;
}

export interface SpotStageEntry {
  entryId: string;
  qty: number;
  pricePerCoinUsd: number;
  executedAt: string;
}

export interface SpotStageInput {
  qty: number;
  pricePerCoinUsd: number;
  executedAt?: string;
}

export interface SpotPosition {
  id: string;
  coinId: string;
  symbol: string;
  name: string;
  image: string;
  isFutures: boolean;
  stages: SpotStageEntry[];
  qty: number;
  totalSpentUsd: number;
  tp1Price: number | null;
  tp2Price: number | null;
  tp3Price: number | null;
  triggered: { tp1: boolean; tp2: boolean; tp3: boolean };
  // Futures close-price target + close/notification lifecycle (null/0 for spot):
  closePrice: number | null;
  closeDirection: 'up' | 'down' | null;
  closedAt: string | null;
  closedAtPrice: number | null;
  notifiedAt: string | null;
  notifyAttempts: number;
  lastNotifyError: string | null;
  createdAt: string;
  updatedAt: string;
  // Enriched (server-side) fields:
  currentPriceUsd: number;
  change24h: number | null;
  avgPriceUsd: number;
  valueUsd: number;
  valueRub: number;
  pnlUsd: number;
  pnlRub: number;
  pnlPct: number;
  sparkline7d: number[];
  /** Set when Bybit and CoinGecko prices for this coin diverge beyond the threshold. */
  priceDivergencePct: number | null;
  tp: { tp1: SpotTpStatus; tp2: SpotTpStatus; tp3: SpotTpStatus };
}

export interface SpotPositionInput {
  id?: string;
  coinId: string;
  symbol: string;
  name: string;
  image?: string;
  isFutures?: boolean;
  qty: number;
  totalSpentUsd: number;
  tp1Price: number | null;
  tp2Price: number | null;
  tp3Price: number | null;
  /** Futures-only: price at which the exchange closes the position. */
  closePrice?: number | null;
  /** Futures-only: fire when price rises ('up') or falls ('down') to closePrice. */
  closeDirection?: 'up' | 'down' | null;
}

export interface SpotConfig {
  freeUsdc: number;
  usdRubManual: number | null;
  telegramSpotAlerts: boolean;
  /** Manually-entered already-realized (fixed) profit in USD. */
  realizedUsd: number;
}

export interface SpotConfigInput {
  freeUsdc?: number;
  usdRubManual?: number | null;
  telegramSpotAlerts?: boolean;
  realizedUsd?: number;
}

/** Aggregate of all futures-flagged positions, returned by the snapshot. */
export interface SpotFuturesSummary {
  count: number;
  valueUsd: number;
  costUsd: number;
  pnlUsd: number;
  pnlRub: number;
}

export interface SpotTotals {
  valueUsd: number;
  costUsd: number;
  pnlUsd: number;
  pnlPct: number;
  valueRub: number;
  pnlRub: number;
}

export interface SpotMarketInfo {
  usdRub: number;
  usdRubSource: 'manual' | 'cbr';
  updatedAt: string;
}

export interface SpotSnapshot {
  positions: SpotPosition[];
  totals: SpotTotals;
  futures: SpotFuturesSummary;
  config: SpotConfig;
  market: SpotMarketInfo;
}
